import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import styled from 'styled-components';
import { Button } from '../../../components/form/Button';
import FormTitle from '../../../components/form/FormTitle';
import CloseIcon from '../../../assets/icons/CloseIcon';
import DocumentIcon from '../../../assets/icons/document-text';
import StyledText from '../../../components/StyledComponents/StyledText';
import { CorporateTheme } from '../../../utils/themes';

import { Field, FieldType, Scope } from '../../../types/types';
import { FormActions, FormContainer, ModalContent, ModalOverlay } from '../../../components/FormBuilder/CustomForm/FormFiller';
import CustomInput from '../../../components/form/CustomInput';
import {
    useAddNewComponentMutation,
    useGetComponentByIdQuery,
    useGetScopesforFieldsQuery,
    useUpdateComponentMutation
} from '../../../api/formsApiSlice';
import AMSSelect from '../../../components/AMSSelect';
import CrossIcon from '../../../assets/icons/CrossIcon';
import { Draggable, DragDropContext, Droppable } from '@hello-pangea/dnd';
import DraggableIcon from '../../../assets/icons/DraggableIcon';
import CustomTextArea from '../../../components/form/CustomTextArea';
import { useToast } from '../../../components/toast/ToastContext';
import { SkeletonCell } from '../../../components/SkeletonCell';
import { useTheme } from '../../../middleware/ThemeContext';
import { CheckboxItem } from '../../../components/listing/ColumnIdentifier';
import { ErrorText } from '../../../components/FormBuilder/FieldSettings/FieldSettingsModal';
import { getIconForDataType } from '../../../components/FormBuilder/FieldSettings/fieldSettingsHelper';

interface CreateComponentModalProps {
    show: boolean;
    onHide: () => void;
    fieldType?: FieldType[];
    onSave: () => void;
    updatingComponentId?: number;
    readOnly?: boolean;
}

interface ComponentField {
    id: string;
    name: string;
    position: number;
    layoutType: 'row' | 'column';
    positionInRow: 'left' | 'right';
    fieldId: string;
}

const FIELDS_PAGE_SIZE = 20;

export const CreateComponentModal: React.FC<CreateComponentModalProps> = ({
    show,
    onHide,
    onSave,
    updatingComponentId,
    readOnly = false,
}) => {
    const { theme } = useTheme();
    const [componentFields, setComponentFields] = useState<ComponentField[]>([]);
    const [componentName, setComponentName] = useState<string>('');
    const [componentDescription, setComponentDescription] = useState<string>('');
    const [allScopes, setAllScopes] = useState<{ label: string; value: string }[]>([]);
    const [selectedScope, setSelectedScope] = useState<string>('');
    const scopesAdditionalParams = useMemo(() => ({ isVisible: true }), []);

    const [componentNameError, setComponentNameError] = useState<string | null>(null);
    const [componentFieldsError, setComponentFieldsError] = useState<string | null>(null);
    const [scopeError, setScopeError] = useState<string | null>(null);
    const [allowMultiple, setAllowMultiple] = useState(false);
    const [maxRepeatCount, setMaxRepeatCount] = useState(1);
    const [addNewComponent, { isLoading: isAdding }] = useAddNewComponentMutation();
    const [updateComponent, { isLoading: isUpdating }] = useUpdateComponentMutation();

    const {
        data: componentData,
        isLoading: isLoadingComponent,
        refetch: refetchComponent
    } = useGetComponentByIdQuery(updatingComponentId || 0, {
        skip: !updatingComponentId,
        refetchOnMountOrArgChange: true
    });

    const showToast = useToast();
    const { data: scopes } = useGetScopesforFieldsQuery({
        isVisible: true
    });

    const scopeIdForDropdown = selectedScope || (componentData?.scopeId ? String(componentData.scopeId) : '');
    const fieldSelectDisabled = !scopeIdForDropdown;
    const fieldSelectParams = scopeIdForDropdown ? { scopeId: scopeIdForDropdown } : undefined;
    const fieldSelectKey = scopeIdForDropdown || 'no-scope';

    const mapFieldsResponse = useCallback((json: any, pageNumber: number) => {
        const fields = json?.fields ?? [];
        const perPage = json?.perPage ?? json?.pagination?.perPage ?? FIELDS_PAGE_SIZE;
        const totalCount = json?.totalCount ?? json?.pagination?.total ?? 0;
        const hasMore = totalCount ? pageNumber * perPage < totalCount : fields.length === perPage;

        const options = fields.map((field: any, index: number) => ({
            label: field?.name,
            value: String(field?.id),
            accessKey: field?.accessKey,
            dataType: field?.dataType,
            icon: getIconForDataType(field?.dataType),
            __idx: index,
            __page: pageNumber,
            ...field,
        }));

        return {
            options,
            hasMore,
            additional: { page: pageNumber + 1 },
        };
    }, []);

    const mapScopesResponse = useCallback((json: any, pageNumber: number) => {
        const scopesList = json?.scopes ?? [];
        const perPage = json?.perPage ?? json?.pagination?.perPage ?? (scopesList.length || 1);
        const totalCount = json?.totalCount ?? json?.pagination?.total ?? scopesList.length;
        const hasMore = totalCount ? pageNumber * perPage < totalCount : scopesList.length === perPage;

        const options = scopesList.map((scope: Scope) => ({
            label: scope.displayName,
            value: String(scope.id ?? ''),
            ...scope,
        }));

        return {
            options,
            hasMore,
            additional: { page: pageNumber + 1 },
        };
    }, []);

    const selectedFieldOptions = useMemo(
        () => componentFields.map((field) => ({
            value: String(field.id),
            label: field.name || `Field ${field.id}`,
        })),
        [componentFields]
    );

    const selectedScopeOption = useMemo(() => {
        if (!selectedScope) return null;
        const existing = allScopes.find((scope) => String(scope.value) === String(selectedScope));
        const label = existing?.label || `Scope ${selectedScope}`;
        return { value: selectedScope, label };
    }, [selectedScope, allScopes]);

    const handleFieldSelectionChange = useCallback((selectedOptions: any) => {
        setComponentFields((prevFields) => {
            const optionsArray = Array.isArray(selectedOptions)
                ? selectedOptions
                : selectedOptions
                    ? [selectedOptions]
                    : [];

            const updatedFields = optionsArray
                .map((option: any, index: number) => {
                    const rawId = option?.id ?? option?.value;
                    if (rawId === undefined || rawId === null) return null;
                    const id = String(rawId);
                    const existingField = prevFields.find((field) => String(field.id) === id);

                    if (existingField) {
                        return {
                            ...existingField,
                            name: option?.label ?? existingField.name,
                            position: index + 1,
                        };
                    }

                    return {
                        id,
                        name: option?.label || `Field ${id}`,
                        layoutType: 'row',
                        positionInRow: 'left',
                        position: index + 1,
                        fieldId: id,
                    } as ComponentField;
                })
                .filter(Boolean) as ComponentField[];

            return updatedFields;
        });
        setComponentFieldsError(null);
    }, [setComponentFieldsError]);

    const handleScopeSelect = (option: any) => {
        const selectedOption = Array.isArray(option) ? option[0] : option;
        const nextValue = selectedOption?.value ? String(selectedOption.value) : '';
        setSelectedScope(nextValue);
        if (nextValue) {
            setScopeError(null);
            if (selectedOption?.label) {
                setAllScopes((prev) => {
                    const exists = prev.some((scope) => String(scope.value) === nextValue);
                    if (exists) return prev;
                    return [...prev, { label: selectedOption.label, value: nextValue }];
                });
            }
        }
    };

    useEffect(() => {
        if (show && updatingComponentId) {
            refetchComponent();
        }
    }, [show, updatingComponentId, refetchComponent]);

    useEffect(() => {
        if (scopes?.scopes) {
            const newOptions = scopes.scopes.map((scope: Scope) => ({
                label: scope.displayName,
                value: scope.id
            }));
            setAllScopes(newOptions);
        }
    }, [scopes]);

    useEffect(() => {
        if (updatingComponentId) return;
        setComponentFields([]);
        setComponentFieldsError(null);
    }, [selectedScope, updatingComponentId]);

    useEffect(() => {
        if (updatingComponentId && componentData) {
            // Populate form with existing component data when in update mode
            setComponentName(componentData.name);
            setComponentDescription(componentData.description || '');
            setSelectedScope(componentData.scopeId ? String(componentData.scopeId) : '');
            setAllowMultiple(Boolean(componentData.maxEntries));
            setMaxRepeatCount(componentData.maxEntries || 1);
            // Map the component fields to our state format
            const mappedFields = componentData?.componentFields.map((componentField: any) => ({
                id: String(componentField?.fieldComponent.id),
                name: componentField?.fieldComponent.name,
                position: componentField?.position,
                layoutType: 'row',
                positionInRow: 'left',
                fieldId: String(componentField?.fieldComponent.id)
            }));

            const orderByPosition = mappedFields.sort(
                (a: ComponentField, b: ComponentField) => a.position - b.position
            );

            setComponentFields(orderByPosition);
        } else {
            // Reset form when creating new component
            setComponentFields([]);
            setComponentName('');
            setComponentDescription('');
            setSelectedScope('');
        }
    }, [updatingComponentId, componentData]);

    const validateForm = () => {
        let isValid = true;

        // Reset errors
        setComponentNameError(null);
        setComponentFieldsError(null);
        setScopeError(null);

        // Validate component name
        if (!componentName.trim()) {
            setComponentNameError('Component name is required');
            isValid = false;
        }

        // Validate fields count
        if (componentFields.length < 2) {
            setComponentFieldsError('At least two fields must be added to the component');
            isValid = false;
        }

        // Validate scope (only for new components)
        if (!updatingComponentId && !selectedScope) {
            setScopeError('Please select a scope for the component');
            isValid = false;
        }

        return isValid;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        const dataToSend = {
            ...(updatingComponentId && { id: updatingComponentId }),
            name: componentName,
            description: componentDescription?.trim() || null,
            // scope: selectedScope || null,
            fields: componentFields.map(field => field.fieldId),
            ...(!updatingComponentId && { scope: selectedScope }),
            allowMultiple: allowMultiple,
            ...(maxRepeatCount && allowMultiple ? { maxEntries: maxRepeatCount } : {}),
        };

        try {
            if (updatingComponentId) {
                await updateComponent(dataToSend).unwrap();
                showToast(
                    'Component Updated',
                    'The component has been updated successfully.',
                    'success'
                );
            } else {
                await addNewComponent(dataToSend).unwrap();
                showToast(
                    'Component Created',
                    'The component has been created successfully.',
                    'success'
                );
            }

            resetForm();
            onHide();
            onSave();
        } catch (err: any) {
            console.error(updatingComponentId ? 'Component update error:' : 'Component creation error:', err);

            const message =
                err?.data?.response?.errors ||
                err?.data?.response?.message ||
                err?.error || err?.data?.message ||
                (updatingComponentId
                    ? 'Failed to update component. Please try again.'
                    : 'Failed to create component. Please try again.');

            showToast(
                updatingComponentId ? 'Component Update Failed' : 'Component Creation Failed',
                message,
                'error'
            );
        }
    };

    const resetForm = () => {
        setComponentFields([]);
        setComponentName('');
        setComponentDescription('');
        setSelectedScope('');
        setAllowMultiple(false);
        setMaxRepeatCount(1);
        setComponentNameError(null);
        setComponentFieldsError(null);
        setScopeError(null);
    };

    const handleCancel = () => {
        resetForm();
        onHide();
    };

    const handleOnDragEnd = (result: any) => {
        const { source, destination } = result;

        if (!destination) return;

        const items = Array.from(componentFields);
        const [reorderedItem] = items.splice(source.index, 1);
        items.splice(destination.index, 0, reorderedItem);

        // Reassign positions
        const updatedItems = items.map((item, index) => ({
            ...item,
            position: index + 1
        }));

        setComponentFields(updatedItems);
    };

    const handleRemoveField = (field: ComponentField) => {
        setComponentFields(prevFields => prevFields.filter(item => item.id !== field.id));
    };

    return (
        <Modal show={show} onHide={onHide} className='border-0'>
            <ModalOverlay>
                <ModalContent theme={theme}>
                    <FormTitle title={readOnly ? 'View Component' : updatingComponentId ? 'Update Component' : 'Create New Component'} />
                    <div style={{ position: 'relative' }}>
                        <FormContainer theme={theme}>
                            <div className="px-3 py-4 d-flex flex-column" style={{ backgroundColor: theme.CorporateTheme.colors.primaryLightBG }}>
                                {!updatingComponentId && (
                                    <>
                                        <div className="mb-3 w-100">
                                            <FieldLabel theme={theme}>
                                                Scope <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                                            </FieldLabel>
                                            <AMSSelect
                                                endpoint="/scopes"
                                                placeholder="Select scope"
                                                value={selectedScopeOption ?? null}
                                                onChange={handleScopeSelect}
                                                mapResponse={mapScopesResponse}
                                                additionalParams={scopesAdditionalParams}
                                                error={scopeError ?? undefined}
                                                isSearchable={false}
                                                defaultOptions
                                                isDisabled={readOnly}
                                            />
                                            {/* {scopeError && <ErrorText theme={theme}>{scopeError}</ErrorText>} */}
                                        </div>
                                    </>
                                )}

                                <div className="mb-2 p-4" style={{ backgroundColor: theme.colors.contentBackground, borderRadius: '8px', border: '1px dashed #BABDC1' }}>
                                    <StyledText className="mb-2 text-center" fontSize="16px" fontWeight={600}>
                                        {updatingComponentId ? 'Update Component Fields' : 'Start Adding Fields to the New Component'}
                                        <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}> *</span>
                                    </StyledText>
                                    <StyledText className="text-center mb-3" color="#585558" fontSize="14px" fontWeight={500}>
                                        {updatingComponentId
                                            ? 'You can select basic and custom fields to update component'
                                            : selectedScope
                                                ? `Fields available for ${allScopes.find(s => s.value === selectedScope)?.label || 'selected scope'}`
                                                : 'Please select a scope first'}
                                    </StyledText>
                                    {isLoadingComponent ? (
                                        <div style={{ marginTop: 8 }}>
                                            <SkeletonCell height="42px" />
                                        </div>
                                    ) : (
                                        <AMSSelect
                                            key={fieldSelectKey}
                                            endpoint="/fields"
                                            placeholder={fieldSelectDisabled ? 'Please select a scope first' : 'Select fields here'}
                                            isMulti
                                            limit={FIELDS_PAGE_SIZE}
                                            value={selectedFieldOptions}
                                            onChange={handleFieldSelectionChange}
                                            additionalParams={fieldSelectParams}
                                            mapResponse={mapFieldsResponse}
                                            isDisabled={fieldSelectDisabled || readOnly}
                                            defaultOptions={!fieldSelectDisabled}
                                            getOptionValue={(option: any) => String(option?.id ?? option?.value ?? '')}
                                            error={componentFieldsError ?? undefined}
                                            showDataType={true}
                                            // showIcons={true}
                                            includeAccessKey={true}
                                        />
                                    )}
                                </div>

                                <div className="mb-4 w-100">
                                    <FieldLabel theme={theme}>
                                        Component Name <span style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>*</span>
                                    </FieldLabel>
                                    {isLoadingComponent ? (
                                        <SkeletonCell height="42px" />
                                    ) : (
                                        <CustomInput
                                            value={componentName ?? ''}
                                            onChange={(val) => {
                                                setComponentName(val);
                                                setComponentNameError(null);
                                            }}
                                            placeholder="Enter component name here"
                                            width="100%"
                                        />
                                    )}
                                    {componentNameError && <ErrorText className='mb-2' theme={theme}>{componentNameError}</ErrorText>}
                                    <div className="my-3 d-flex align-items-center justify-content-between">
                                        <CheckboxItem theme={theme} className='py-1'>
                                            <input
                                                type="checkbox"
                                                checked={allowMultiple}
                                                onChange={(e) => {
                                                    setAllowMultiple(e.target.checked);
                                                    if (!e.target.checked) setMaxRepeatCount(1);
                                                }}
                                            />
                                            <StyledText >Allow Multiple</StyledText>
                                        </CheckboxItem>

                                        {allowMultiple && (
                                            <div className="d-flex align-items-center gap-2">
                                                <MaximumEntriesSelect theme={theme}
                                                    value={maxRepeatCount}
                                                    onChange={(e) => setMaxRepeatCount(Number(e.target.value))}
                                                >
                                                    {[...Array(10)].map((_, i) => (
                                                        <option key={i + 2} value={i + 2}>
                                                            {i + 2}
                                                        </option>
                                                    ))}

                                                </MaximumEntriesSelect>
                                                <span style={{ fontSize: '0.85rem' }}>Maximum Entries</span>
                                            </div>
                                        )}
                                    </div>

                                    <DragDropContext onDragEnd={handleOnDragEnd}>
                                        <Droppable droppableId="componentFields">
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.droppableProps}
                                                >
                                                    {isLoadingComponent ? (
                                                        // show a few skeleton chips while loading existing component fields
                                                        [1, 2, 3].map((i) => (
                                                            <ComponentFieldChips key={i} theme={theme} className="mb-3">
                                                                <DraggableIconContainer>
                                                                    <DraggableIcon color={theme.CorporateTheme.colors.lightSecondary} />
                                                                </DraggableIconContainer>
                                                                <div style={{ flex: 1 }}>
                                                                    <SkeletonCell height="14px" />
                                                                </div>
                                                                <CrossIconContainer>
                                                                    <CrossIcon size={20} />
                                                                </CrossIconContainer>
                                                            </ComponentFieldChips>
                                                        ))
                                                    ) : (
                                                        componentFields.length > 0 && componentFields.map((field, index) => (
                                                            <Draggable key={field.id} draggableId={String(field.id)} index={index}>
                                                                {(provided) => (
                                                                    <ComponentFieldChips
                                                                        theme={theme}
                                                                        className="mb-3"
                                                                        ref={provided.innerRef}
                                                                        {...provided.draggableProps}
                                                                        {...provided.dragHandleProps}
                                                                    >
                                                                        <DraggableIconContainer>
                                                                            <DraggableIcon color={theme.CorporateTheme.colors.lightSecondary} />
                                                                        </DraggableIconContainer>
                                                                        <span>{field.name || `Field ${field.id}`}</span>
                                                                        <CrossIconContainer onClick={() => handleRemoveField(field)}>
                                                                            <CrossIcon size={20} />
                                                                        </CrossIconContainer>
                                                                    </ComponentFieldChips>
                                                                )}
                                                            </Draggable>
                                                        ))
                                                    )}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>

                                    <FieldLabel theme={theme}>
                                        Component Description
                                    </FieldLabel>
                                    <CustomTextArea
                                        value={componentDescription ?? ''}
                                        onChange={(val) => setComponentDescription(val)}
                                        placeholder="Enter component description here"
                                    />
                                </div>
                            </div>
                        </FormContainer>
                        {readOnly && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'transparent',
                                zIndex: 20,
                                pointerEvents: 'all'
                            }} />
                        )}
                    </div>
                    <FormActions>
                        <Button theme={theme} type="button" $variant="cancel" onClick={handleCancel} disabled={isAdding}>
                            <CloseIcon color={theme.CorporateTheme.colors.textSecondary} /> Cancel
                        </Button>
                        {!readOnly && (
                            <Button
                                theme={theme}
                                type="submit"
                                disabled={isAdding}
                                $variant="add"
                                onClick={handleSave}
                            >
                                <DocumentIcon color={theme.CorporateTheme.colors.background} />
                                {isUpdating || isAdding
                                    ? (updatingComponentId ? 'Updating...' : 'Creating...')
                                    : (updatingComponentId ? 'Update Component' : 'Create Component')}
                            </Button>
                        )}
                    </FormActions>
                </ModalContent>
            </ModalOverlay>
        </Modal>
    );
};

// Styled Components
export const FieldLabel = styled(StyledText)(({ theme }) => ({
    fontSize: '14px',
    fontWeight: 600,
    color: theme.CorporateTheme.colors.headerColor,
    margin: '10px 0',
}));

export const ComponentFieldChips = styled('div')(({ theme }) => ({
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: '8px',
    background: theme.CreateFormTheme.colors.inputBackground,
    padding: '8px',
    borderRadius: '4px',
    border: `1px solid ${theme.CreateFormTheme.colors.inputBorders}`,
    height: '42px',
    color: theme.CorporateTheme.colors.headerColor,
}));

export const DraggableIconContainer = styled.div`
  cursor: grab;
`;

export const CrossIconContainer = styled.div`
  cursor: pointer; 
  margin-left: auto;
`;
const MaximumEntriesSelect = styled('select')(({ theme }) => ({
    fontSize: '14px',
    fontFamily: "'Lato', sans-serif",
    color: theme.CorporateTheme.colors.paginationSelect,
    backgroundColor: theme.colors.contentBackground,
    border: `1px solid ${theme.CorporateTheme.colors.inputBorders}`,
    padding: '4px',
    borderRadius: '4px',
}));

export default CreateComponentModal;